"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, ApiError, errorMessage, mutation } from "@/lib/api";
import type { PanelContext, User, Tenant } from "@/lib/types";

type Session = {
  user: User | null;
  tenant: Tenant | null;
  permissions: string[];
  discordLinked: boolean;
  panel: PanelContext | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
  login: (login: string, password: string, code?: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
};
const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [discordLinked, setDiscordLinked] = useState(false);
  const [panel, setPanel] = useState<PanelContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++generation.current;
    try {
      const selectedPanel = await api<PanelContext>("/panel/context");
      if (current !== generation.current) return;
      setPanel(selectedPanel);
      const session = await api<{ user: User; permissions: string[]; tenant: Tenant | null; discordLinked?: boolean }>(
        "/auth/me",
      );
      if (current !== generation.current) return;
      setUser(session.user);
      setTenant(session.tenant);
      setPermissions(session.permissions);
      setDiscordLinked(!!session.discordLinked);
      setError("");
    } catch (error) {
      if (current !== generation.current) return;
      if (!(error instanceof ApiError && error.status === 401)) setPanel(null);
      setUser(null);
      setTenant(null);
      setPermissions([]);
      setDiscordLinked(false);
      setError(
        error instanceof ApiError && error.status === 401
          ? ""
          : error instanceof ApiError && error.status === 404
            ? "This workspace is unavailable."
            : errorMessage(error),
      );
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    const initial = setTimeout(() => void refresh(), 0);
    const expired = () => {
      generation.current++;
      setUser(null);
      setTenant(null);
      setPermissions([]);
      setDiscordLinked(false);
      setLoading(false);
    };
    window.addEventListener("velnqo:unauthorized", expired);
    const changed = () => {
      setLoading(true);
      void refresh();
    };
    window.addEventListener("velnqo:session-changed", changed);
    const focused = () => {
      void refresh();
    };
    const visible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", focused);
    document.addEventListener("visibilitychange", visible);
    return () => {
      clearTimeout(initial);
      window.removeEventListener("velnqo:unauthorized", expired);
      window.removeEventListener("velnqo:session-changed", changed);
      window.removeEventListener("focus", focused);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refresh]);
  const login = useCallback(
    async (login: string, password: string, code?: string) => {
      await mutation("/auth/login", { login, password, code });
      await refresh();
    },
    [refresh],
  );
  const logout = useCallback(async () => {
    generation.current++;
    await mutation("/auth/logout", {});
    generation.current++;
    setUser(null);
    setTenant(null);
    setPermissions([]);
    setDiscordLinked(false);
    setError("");
  }, []);
  const can = useCallback(
    (permission: string) =>
      !!user?.isAdmin ||
      permissions.includes("*") ||
      permissions.includes(permission),
    [user, permissions],
  );
  const value = useMemo(
    () => ({ user, tenant, permissions, discordLinked, panel, loading, error, refresh, login, logout, can }),
    [user, tenant, permissions, discordLinked, panel, loading, error, refresh, login, logout, can],
  );
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
export function useSession() {
  const session = useContext(SessionContext);
  if (!session) throw new Error("SessionProvider is required");
  return session;
}
